/**
 * MONSTER TOWN — ART KIT. ARCHITECT-OWNED SHARED. Additive only.
 *
 * The shared cel-shaded look and the shared GPU resources. EVERY art builder
 * uses this. Two reasons, both non-negotiable:
 *  1. Consistency — six crews building props independently must land in one
 *     visual family (LAW #5, it must always look good).
 *  2. Older iPad — geometry and materials here are CACHED AND SHARED. A tree
 *     built 200 times must not create 200 materials. If you call
 *     `new THREE.MeshStandardMaterial()` in a prop, you have made the game
 *     slower for a 3-year-old.
 *
 * ADDING TO THE KIT IS ENCOURAGED. Add a new factory; never change an existing
 * one's output, other crews are matching against it.
 */

import * as THREE from 'three'
import { ART, PALETTE, hex, type PaletteName } from '../core/palette'

// ---------------------------------------------------------------------------
// toon gradient
// ---------------------------------------------------------------------------

let gradientMap: THREE.DataTexture | null = null

function toonGradient(): THREE.DataTexture {
  if (gradientMap !== null) return gradientMap
  const bands = ART.toonBands
  const data = new Uint8Array(bands * 4)
  for (let i = 0; i < bands; i++) {
    // Shadowed band stays warm rather than grey — cosy, not gloomy.
    const v = Math.round(255 * (0.55 + (0.45 * i) / Math.max(1, bands - 1)))
    data[i * 4 + 0] = v
    data[i * 4 + 1] = Math.round(v * 0.985)
    data[i * 4 + 2] = Math.round(v * 0.96)
    data[i * 4 + 3] = 255
  }
  const tex = new THREE.DataTexture(data, bands, 1, THREE.RGBAFormat)
  tex.minFilter = THREE.NearestFilter
  tex.magFilter = THREE.NearestFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  gradientMap = tex
  return tex
}

// ---------------------------------------------------------------------------
// materials (cached)
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.Material>()

/** The standard cel-shaded surface. Use this for ~everything. */
export function toon(color: PaletteName | number, opts: { flat?: boolean } = {}): THREE.Material {
  const c = typeof color === 'number' ? color : hex(color)
  const key = `toon:${c}:${opts.flat === true ? 1 : 0}`
  const cached = matCache.get(key)
  if (cached !== undefined) return cached
  const m = new THREE.MeshToonMaterial({
    color: c,
    gradientMap: toonGradient(),
    ...(opts.flat === true ? { flatShading: true } : {}),
  })
  matCache.set(key, m)
  return m
}

/** Unlit flat colour — UI-ish props, ghosts, glowing lamps. Cheapest option. */
export function flat(color: PaletteName | number, opacity = 1): THREE.Material {
  const c = typeof color === 'number' ? color : hex(color)
  const key = `flat:${c}:${opacity}`
  const cached = matCache.get(key)
  if (cached !== undefined) return cached
  const m = new THREE.MeshBasicMaterial({
    color: c,
    ...(opacity < 1 ? { transparent: true, opacity } : {}),
  })
  matCache.set(key, m)
  return m
}

/** Glass/water — translucent, no shadows. */
export function glassy(color: PaletteName | number, opacity = 0.7): THREE.Material {
  const c = typeof color === 'number' ? color : hex(color)
  const key = `glass:${c}:${opacity}`
  const cached = matCache.get(key)
  if (cached !== undefined) return cached
  const m = new THREE.MeshToonMaterial({
    color: c,
    gradientMap: toonGradient(),
    transparent: true,
    opacity,
  })
  matCache.set(key, m)
  return m
}

/** Build-mode ghost material. Green = it fits, red = it does not. */
export function ghostMaterial(valid: boolean): THREE.Material {
  return flat(valid ? 'uiGhostOk' : 'uiGhostBad', 0.55)
}

// ---------------------------------------------------------------------------
// geometry (cached)
// ---------------------------------------------------------------------------

const geoCache = new Map<string, THREE.BufferGeometry>()

function cachedGeo(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
  const c = geoCache.get(key)
  if (c !== undefined) return c
  const g = make()
  geoCache.set(key, g)
  return g
}

export function boxGeo(w: number, h: number, d: number): THREE.BufferGeometry {
  return cachedGeo(`box:${w}:${h}:${d}`, () => new THREE.BoxGeometry(w, h, d))
}

export function cylGeo(r: number, h: number, seg = 10): THREE.BufferGeometry {
  return cachedGeo(`cyl:${r}:${h}:${seg}`, () => new THREE.CylinderGeometry(r, r, h, seg))
}

export function coneGeo(r: number, h: number, seg = 10): THREE.BufferGeometry {
  return cachedGeo(`cone:${r}:${h}:${seg}`, () => new THREE.ConeGeometry(r, h, seg))
}

export function sphereGeo(r: number, seg = 12): THREE.BufferGeometry {
  return cachedGeo(`sph:${r}:${seg}`, () => new THREE.SphereGeometry(r, seg, Math.max(6, seg >> 1)))
}

/**
 * A partial torus in the XY plane, axis on +Z — i.e. a stroke you can lay flat
 * on the FRONT of something. `arc` in radians, starting at +X and sweeping
 * counter-clockwise. Half a torus is a mouth; a full one is a ring.
 *
 * Added for the monster smile: a crescent built from a dark ellipsoid partly
 * hidden by a body-coloured one is two draw calls and a visible shading seam
 * where the two toon surfaces meet, and a smile made of three separate dots is
 * three. This is one mesh, one cached geometry, and it is a real curve.
 */
export function torusGeo(tube = 0.18, radialSeg = 6, tubularSeg = 14, arc = Math.PI): THREE.BufferGeometry {
  return cachedGeo(`tor:${tube}:${radialSeg}:${tubularSeg}:${arc}`, () =>
    new THREE.TorusGeometry(0.5, tube * 0.5, radialSeg, tubularSeg, arc),
  )
}

/**
 * An UPRIGHT quad in the XY plane, facing +Z. `planeGeo` is laid flat for
 * floors; this is the one for cards, halos and anything screen-facing.
 */
export function quadGeo(w: number, h: number): THREE.BufferGeometry {
  return cachedGeo(`quad:${w}:${h}`, () => new THREE.PlaneGeometry(w, h))
}

export function planeGeo(w: number, d: number): THREE.BufferGeometry {
  return cachedGeo(`pln:${w}:${d}`, () => {
    const g = new THREE.PlaneGeometry(w, d)
    g.rotateX(-Math.PI / 2)
    return g
  })
}

// ---------------------------------------------------------------------------
// mesh helpers — the chunky vocabulary every prop is built from
// ---------------------------------------------------------------------------

export interface BoxOpts {
  color: PaletteName | number
  x?: number
  y?: number
  z?: number
  name?: string
  flat?: boolean
  rotY?: number
}

/** A chunky box. `y` is the CENTRE, as three.js means it. */
export function box(w: number, h: number, d: number, o: BoxOpts): THREE.Mesh {
  const m = new THREE.Mesh(boxGeo(w, h, d), toon(o.color, o.flat === true ? { flat: true } : {}))
  m.position.set(o.x ?? 0, o.y ?? 0, o.z ?? 0)
  if (o.rotY !== undefined) m.rotation.y = o.rotY
  if (o.name !== undefined) m.name = o.name
  m.castShadow = true
  m.receiveShadow = true
  return m
}

/** A box sitting ON the floor — `y` is the BOTTOM. The common case for props. */
export function boxOn(w: number, h: number, d: number, o: BoxOpts): THREE.Mesh {
  return box(w, h, d, { ...o, y: (o.y ?? 0) + h / 2 })
}

export function cylinder(r: number, h: number, o: BoxOpts & { seg?: number }): THREE.Mesh {
  const m = new THREE.Mesh(cylGeo(r, h, o.seg ?? 10), toon(o.color))
  m.position.set(o.x ?? 0, (o.y ?? 0) + h / 2, o.z ?? 0)
  if (o.name !== undefined) m.name = o.name
  m.castShadow = true
  m.receiveShadow = true
  return m
}

export function cone(r: number, h: number, o: BoxOpts & { seg?: number }): THREE.Mesh {
  const m = new THREE.Mesh(coneGeo(r, h, o.seg ?? 10), toon(o.color))
  m.position.set(o.x ?? 0, (o.y ?? 0) + h / 2, o.z ?? 0)
  if (o.name !== undefined) m.name = o.name
  m.castShadow = true
  return m
}

export function ball(r: number, o: BoxOpts & { seg?: number }): THREE.Mesh {
  const m = new THREE.Mesh(sphereGeo(r, o.seg ?? 12), toon(o.color))
  m.position.set(o.x ?? 0, o.y ?? 0, o.z ?? 0)
  if (o.name !== undefined) m.name = o.name
  m.castShadow = true
  m.receiveShadow = true
  return m
}

/** A named group — the standard root every art builder returns. */
export function group(name: string): THREE.Group {
  const g = new THREE.Group()
  g.name = name
  return g
}

// ---------------------------------------------------------------------------
// ink outlines (inverted hull)
// ---------------------------------------------------------------------------

let outlinesEnabled = true
const outlineMat = new THREE.MeshBasicMaterial({ color: hex('ink'), side: THREE.BackSide })

/**
 * Perf lever: the renderer turns outlines off on the low quality tier.
 *
 * IT NOW WORKS AFTER THE ART IS BUILT. This only set a flag that `inked()` and
 * `inkAll()` read at BUILD time, so dropping to the low tier on a struggling
 * iPad changed nothing at all — the town's outline shells were already baked
 * into its merged clusters, and `probe-town-cost` confirmed it: "quality low (no
 * outlines, no shadows)" reported exactly the same draw count and triangle count
 * as high. The lever the renderer leans on when frames run long was a no-op.
 *
 * Every shell in the game shares this one material, and `Material.visible`
 * skips the draw entirely, so one assignment drops every outline draw call and
 * every outline triangle at runtime — which is what the tier was always meant
 * to do.
 */
export function setOutlinesEnabled(on: boolean): void {
  outlinesEnabled = on
  outlineMat.visible = on
}

export function outlinesAreEnabled(): boolean {
  return outlinesEnabled
}

/**
 * Add a soft ink outline around a mesh. Doubles that mesh's draw call, so use
 * it on SILHOUETTE pieces only (the body of a thing), never on every detail.
 */
export function inked(mesh: THREE.Mesh, width: number = ART.outlineWidth): THREE.Object3D {
  if (!outlinesEnabled) return mesh
  const g = new THREE.Group()
  g.name = `${mesh.name || 'mesh'}:inked`
  const shell = new THREE.Mesh(mesh.geometry, outlineMat)
  shell.position.copy(mesh.position)
  shell.rotation.copy(mesh.rotation)
  shell.scale.copy(mesh.scale).multiplyScalar(1)
  // Scale up in world units rather than percent so small props keep a fine line.
  const bbox = new THREE.Box3().setFromBufferAttribute(
    mesh.geometry.getAttribute('position') as THREE.BufferAttribute,
  )
  const size = bbox.getSize(new THREE.Vector3())
  shell.scale.set(
    1 + (width * 2) / Math.max(0.001, size.x),
    1 + (width * 2) / Math.max(0.001, size.y),
    1 + (width * 2) / Math.max(0.001, size.z),
  )
  shell.castShadow = false
  shell.receiveShadow = false
  shell.renderOrder = -1
  g.add(shell, mesh)
  return g
}

/**
 * INK A MESH THAT IS ABOUT TO BE SCALED. ADDITIVE — `inked()` is untouched.
 *
 * `inked()` derives the shell from the mesh's GEOMETRY size and then the caller
 * scales the returned wrapper, so the ink is scaled with it: a part built from a
 * unit sphere and scaled to 0.2 wide gets an outline 0.2x the intended width.
 * On the monster rig, where every part is a unit ball scaled to size, that made
 * the torso's line 0.030 world units and an arm's 0.007 — invisible. The
 * round-2 critic read the result exactly: "the ink outline breaks where the arms
 * overlap the body (both arms have no stroke at all)".
 *
 * Here the scale is applied to the mesh FIRST and divided back out of the shell
 * ratio, so `width` means the same number of world units on every part however
 * small it is. Returns the wrapper to position; do not scale it again.
 */
export function inkedScaled(
  mesh: THREE.Mesh,
  sx: number,
  sy: number,
  sz: number,
  width: number = ART.outlineWidth,
): THREE.Object3D {
  mesh.scale.set(sx, sy, sz)
  if (!outlinesEnabled) return mesh
  const g = new THREE.Group()
  g.name = `${mesh.name || 'mesh'}:inked`
  const shell = new THREE.Mesh(mesh.geometry, outlineMat)
  shell.position.copy(mesh.position)
  shell.rotation.copy(mesh.rotation)
  const bbox = new THREE.Box3().setFromBufferAttribute(
    mesh.geometry.getAttribute('position') as THREE.BufferAttribute,
  )
  const size = bbox.getSize(new THREE.Vector3())
  // world half-extent + width  ==  (size/2) * s * ratio   =>   ratio = 1 + 2w/(size*|s|)
  shell.scale.set(
    sx * (1 + (width * 2) / Math.max(0.001, size.x * Math.abs(sx))),
    sy * (1 + (width * 2) / Math.max(0.001, size.y * Math.abs(sy))),
    sz * (1 + (width * 2) / Math.max(0.001, size.z * Math.abs(sz))),
  )
  shell.castShadow = false
  shell.receiveShadow = false
  shell.renderOrder = -1
  g.add(shell, mesh)
  return g
}

/**
 * Opt a mesh OUT of `inkAll`. Smoke puffs, glass and glowing bulbs must never
 * get a hard ink line round them — they are not silhouette, they are light.
 */
export function noInk<T extends THREE.Object3D>(o: T): T {
  o.userData['noInk'] = true
  return o
}

/**
 * INK A WHOLE PROP, not one box of it. ADDITIVE — `inked()` is untouched.
 *
 * WHY THIS EXISTS: `inked()` outlines ONE mesh, so a prop built from four boxes
 * got a line round the widest box and nothing round the rest. The round-2 critic
 * read exactly that: "the fridge outlined on its left and top-right silhouette
 * but not its bottom or right-front vertical, and a free-floating outline
 * segment next to the stove joined to nothing", plus trees outlined next to
 * bushes that were not. An inverted hull only closes if EVERY part of the
 * silhouette carries one; a partial pass is worse than none, because the eye
 * reads the missing half as a rendering fault.
 *
 * Cost is one extra draw call per merged cluster (every shell shares the one
 * outline material, so `mergeStatic` collapses them all into a single mesh) and
 * a doubling of triangles on chunky boxes, which are 12 triangles each.
 */
// `width: number`, not the inferred literal. `ART` is `as const`, so an
// unannotated default narrowed this parameter to the type `0.035` and any
// caller computing a width (scenery that scale-compensates its ink) failed to
// compile. Widening the annotation changes no output, only what typechecks.
export function inkAll<T extends THREE.Object3D>(root: T, width: number = ART.outlineWidth): T {
  if (!outlinesEnabled) return root
  const targets: THREE.Mesh[] = []
  root.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh !== true) return
    if (m.userData['noInk'] === true) return
    const mat = Array.isArray(m.material) ? m.material[0] : m.material
    if (mat === undefined || mat === outlineMat) return
    // Translucent things (glass, smoke, ghosts) are light, not silhouette.
    if ((mat as THREE.Material).transparent === true) return
    targets.push(m)
  })
  const bbox = new THREE.Box3()
  const size = new THREE.Vector3()
  for (const m of targets) {
    const parent = m.parent
    if (parent === null) continue
    const pos = m.geometry.getAttribute('position')
    if (pos === undefined) continue
    bbox.setFromBufferAttribute(pos as THREE.BufferAttribute)
    bbox.getSize(size)
    const shell = new THREE.Mesh(m.geometry, outlineMat)
    shell.position.copy(m.position)
    shell.rotation.copy(m.rotation)
    shell.scale.set(
      m.scale.x * (1 + (width * 2) / Math.max(0.001, size.x)),
      m.scale.y * (1 + (width * 2) / Math.max(0.001, size.y)),
      m.scale.z * (1 + (width * 2) / Math.max(0.001, size.z)),
    )
    shell.castShadow = false
    shell.receiveShadow = false
    shell.renderOrder = -1
    shell.name = `${m.name || 'mesh'}:ink`
    parent.add(shell)
  }
  return root
}

// ---------------------------------------------------------------------------
// code-generated textures
// ---------------------------------------------------------------------------

const texCache = new Map<string, THREE.Texture>()

/**
 * Generate a texture in code (zero external assets rule). `draw` gets a 2D
 * context of `ART.textureSize` square. Results are cached by `key`.
 */
export function generatedTexture(
  key: string,
  draw: (c: CanvasRenderingContext2D, size: number) => void,
): THREE.Texture {
  const cached = texCache.get(key)
  if (cached !== undefined) return cached
  const size = ART.textureSize
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new Error('no 2d context for generated texture')
  draw(ctx, size)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 1
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  texCache.set(key, tex)
  return tex
}

/**
 * An unlit material carrying a code-generated texture, cached like every other
 * material here. ADDITIVE.
 *
 * Exists so soft, gradient-edged things — a glow, a contact shadow, a vignette —
 * can be one quad instead of a flattened sphere. A flattened `ball()` used as a
 * disc shows its segment count as a visible polygon edge: the round-2 critic
 * read the creator backdrop as "two concentric flat polygons, visibly faceted
 * decagons". A gradient texture has no facets at any size and costs one quad.
 *
 * `alphaFromLuma` builds the alpha channel from the drawing's brightness, so a
 * black-to-white radial gradient becomes an opaque-to-transparent one and the
 * edge dissolves instead of ending.
 */
export function texturedFlat(
  key: string,
  draw: (c: CanvasRenderingContext2D, size: number) => void,
  opts: { color?: PaletteName | number; opacity?: number; size?: number } = {},
): THREE.Material {
  const tint = opts.color === undefined ? 0xffffff : typeof opts.color === 'number' ? opts.color : hex(opts.color)
  const opacity = opts.opacity ?? 1
  const matKey = `texflat:${key}:${tint}:${opacity}`
  const cached = matCache.get(matKey)
  if (cached !== undefined) return cached
  const m = new THREE.MeshBasicMaterial({
    map: generatedTexture(key, draw),
    color: tint,
    transparent: true,
    opacity,
    depthWrite: false,
  })
  matCache.set(matKey, m)
  return m
}

/**
 * Paint a soft radial blob — opaque in the middle, dissolved at the rim. Pass
 * this to `texturedFlat` for halos, glows and contact shadows.
 */
export function softDisc(hold = 0.62): (c: CanvasRenderingContext2D, s: number) => void {
  return (c, s) => {
    const r = s / 2
    const g = c.createRadialGradient(r, r, 0, r, r, r)
    g.addColorStop(0, 'rgba(255,255,255,1)')
    g.addColorStop(hold, 'rgba(255,255,255,0.94)')
    g.addColorStop(1, 'rgba(255,255,255,0)')
    c.clearRect(0, 0, s, s)
    c.fillStyle = g
    c.beginPath()
    c.arc(r, r, r, 0, Math.PI * 2)
    c.fill()
  }
}

/** Soft speckled ground texture — the base look of grass, dirt, sand. */
export function speckleTexture(base: PaletteName, speck: PaletteName, density = 90): THREE.Texture {
  return generatedTexture(`speckle:${base}:${speck}:${density}`, (c, s) => {
    c.fillStyle = PALETTE[base]
    c.fillRect(0, 0, s, s)
    c.fillStyle = PALETTE[speck]
    let seed = 12345
    const rnd = (): number => {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
      return seed / 0x100000000
    }
    for (let i = 0; i < density; i++) {
      const x = rnd() * s
      const y = rnd() * s
      const r = 1 + rnd() * 2.5
      c.globalAlpha = 0.25 + rnd() * 0.35
      c.beginPath()
      c.ellipse(x, y, r, r * 0.7, rnd() * Math.PI, 0, Math.PI * 2)
      c.fill()
    }
    c.globalAlpha = 1
  })
}

// ---------------------------------------------------------------------------
// budget accounting
// ---------------------------------------------------------------------------

export function kitStats(): { materials: number; geometries: number; textures: number } {
  return { materials: matCache.size, geometries: geoCache.size, textures: texCache.size }
}

/** Dispose everything cached. Only on a full teardown. */
export function disposeKit(): void {
  for (const m of matCache.values()) m.dispose()
  for (const g of geoCache.values()) g.dispose()
  for (const t of texCache.values()) t.dispose()
  matCache.clear()
  geoCache.clear()
  texCache.clear()
  gradientMap?.dispose()
  gradientMap = null
}
